import { useState, forwardRef, useRef, useCallback, useMemo } from 'react';

import { Popper, ClickAwayListener } from '@mui/base';
import { Option, type OptionRootSlotProps } from '@mui/base/Option';
import { useSelect, SelectProvider } from '@mui/base/useSelect';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import type { ButtonProps } from '@vakantiesnl/components/src/atoms/Button/Button.types';
import { Icon, type IconTypes, type IconProps } from '@vakantiesnl/components/src/atoms/Icon';

import { useCustomStyles } from './ButtonDropdown.style';
import { Typography } from '../../atoms/Typography';

export type ButtonDropdownProps = {
	/**
	 * Text to be displayed on the button.
	 */
	buttonText: string;
	/**
	 * Variant of the button to be used.
	 */
	buttonVariant: ButtonProps['variant'];
	/**
	 * Icon type to display in the button
	 * */
	iconType: IconTypes;
	/**
	 * Size of the icon to be displayed in the button.
	 */
	iconSize: IconProps['size'];
	/**
	 * Callback function that is invoked when an option is selected.
	 */
	onOptionClick?: (option: string) => void;
	/**
	 * Array of options that will be displayed in the dropdown.
	 */
	options: Option[];
	/**
	 * Optional className to customize the styling of the list box in the dropdown.
	 */
	listBoxClassName?: string;
	/**
	 * Optional className for the li's
	 * */
	className?: string;
	/**
	 * The currently selected value in the dropdown.
	 */
	selectedValue?: string;
};

interface Option {
	group: string;
	name: string;
	label: string;
	value: string;
	hint: string;
}

export const ButtonDropdown = ({
	iconType,
	iconSize,
	buttonVariant,
	buttonText,
	onOptionClick,
	options,
	listBoxClassName,
	className,
	selectedValue,
}: ButtonDropdownProps): JSX.Element => {
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const listboxRef = useRef<HTMLUListElement | null>(null);

	const { classes, cx } = useCustomStyles();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [active, setActive] = useState<boolean>(false);

	const open = Boolean(anchorEl);

	const handleClick = useCallback((event: React.MouseEvent<HTMLElement>): void => {
		setAnchorEl((anchorEl) => (anchorEl ? null : event.currentTarget));
		setActive((prev) => !prev);
	}, []);

	const handleClickOutside = useCallback((event: MouseEvent | TouchEvent): void => {
		if (buttonRef.current?.contains(event.target as Node)) {
			return;
		}
		setAnchorEl(null);
		setActive(false);
	}, []);

	const handleSelectChange = (_: unknown, selectedValue: string | null): void => {
		setAnchorEl(null);
		setActive(false);
		if (selectedValue !== null) {
			onOptionClick?.(selectedValue);
		}
	};

	const { getListboxProps, contextValue } = useSelect({
		options: options,
		multiple: false,
		onChange: handleSelectChange,
		open: active,
		listboxRef,
		buttonRef,
	});

	const modifiers = useMemo(
		() => [
			{
				name: 'flip',
				enabled: false,
			},
		],
		[],
	);

	const items = useMemo(
		() => ({
			root: forwardRef<HTMLLIElement, OptionRootSlotProps<string>>(function OptionItem(
				{ children, ownerState, ...props },
				ref,
			) {
				return (
					<li {...props} className={cx(classes.listItem, classes.defaultList, className)} ref={ref}>
						<Typography variant="labelMd" as="span">
							{children}
						</Typography>
					</li>
				);
			}),
		}),
		[classes.listItem, classes.defaultList, className, cx],
	);

	return (
		<div className={classes.wrapperDiv} data-cy="buttonDropdownContainer">
			<Button
				variant={buttonVariant}
				ref={buttonRef}
				active={active}
				onClick={handleClick}
				className={classes.button}
				data-cy="sortingOrderDropdown"
			>
				<Icon size={iconSize} type={iconType} />
				{buttonText}
			</Button>
			<ClickAwayListener onClickAway={handleClickOutside}>
				<Popper
					open={open}
					placement="bottom"
					disablePortal={true}
					className={classes.popper}
					anchorEl={anchorEl}
					modifiers={modifiers}
				>
					<ul {...getListboxProps()} ref={listboxRef} className={cx(classes.listBox, listBoxClassName)}>
						<SelectProvider value={contextValue}>
							{options.map((option, index) => (
								<Option
									key={index}
									value={option.value}
									slots={items}
									disabled={false}
									label={option.label}
									data-cy={`${option.value}`}
								>
									{option.label === selectedValue ? (
										<div className={classes.selectedOption}>
											<Icon type="checkmark" className={classes.checkmark} />
											{option.label}
										</div>
									) : (
										option.label
									)}
								</Option>
							))}
						</SelectProvider>
					</ul>
				</Popper>
			</ClickAwayListener>
		</div>
	);
};
